"use client";

import * as React from "react";
import { CalendarDays } from "lucide-react";
import { DayButton } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

// ✅ Date → yyyy-MM-dd 
const formatToISODate = (date: Date): string => {
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const d = date.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// ✅ Date → dd/MM/พ.ศ. (สำหรับ UI)
const formatToThaiDate = (date: Date): string => {
  if (!date || isNaN(date.getTime())) return "";
  const d = date.getDate().toString().padStart(2, "0");
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const y = date.getFullYear() + 543;
  return `${d}/${m}/${y}`;
};

type RdpDropdownOption = { value: number; label: string };
type RdpDropdownProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  options?: RdpDropdownOption[];
};

interface DatePickerProps {
  label?: string;
  placeholder?: string;
  value?: string; // yyyy-MM-dd
  onChange?: (isoDate: string) => void;
  disabled?: boolean;
  minDate?: Date; // เพิ่ม minDate สำหรับจำกัดวันที่ขั้นต่ำ
  className?: string;
  visitCounts?: Record<string, number>;
  onOpenChange?: (open: boolean) => void;
  dayStyles?: {
    selectedBg?: string;
    selectedText?: string;
    hoverBg?: string;
    hoverText?: string;
    badgeBg?: string;
    badgeText?: string;
    todayRing?: string;
  };
}

export function DatePicker({
  label,
  placeholder = "เลือกวันที่",
  value,
  onChange,
  disabled,
  minDate,
  className = "w-full",
  visitCounts = {},
  onOpenChange,
  dayStyles,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const styles = {
    selectedBg: dayStyles?.selectedBg ?? "bg-primary",
    selectedText: dayStyles?.selectedText ?? "text-primary-foreground",
    hoverBg: dayStyles?.hoverBg ?? "hover:bg-accent",
    hoverText: dayStyles?.hoverText ?? "hover:text-accent-foreground",
    badgeBg: dayStyles?.badgeBg ?? "bg-blue-500",
    badgeText: dayStyles?.badgeText ?? "text-white",
    todayRing: dayStyles?.todayRing ?? "ring-1 ring-primary/60",
  };

  const selectedDate = React.useMemo(() => {
    if (!value) return undefined;
    const d = new Date(value + "T00:00:00");
    return isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) onOpenChange?.(newOpen);
  };

  const TH_WEEKDAYS = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];

  const CustomDayButton = ({
    className,
    day,
    modifiers,
    ...rest
  }: React.ComponentProps<typeof DayButton>) => {
    const dateStr = formatToISODate(day.date);
    const count = visitCounts[dateStr];

    const rootClass = [
      "w-11 h-11 p-0 rounded-lg transition",
      styles.hoverBg,
      styles.hoverText,
      modifiers?.selected ? `${styles.selectedBg} ${styles.selectedText}` : "",
      modifiers?.today ? styles.todayRing : "",
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <DayButton
        {...rest}
        className={rootClass}
        day={day}
        modifiers={modifiers}
      >
        <div className="flex flex-col items-center justify-center w-full h-full gap-1">
          <span className="text-sm font-medium">{day.date.getDate()}</span>

          {count ? (
            <span
              className={`inline-flex items-center justify-center min-w-[1.15rem] h-[1.15rem] rounded-full ${styles.badgeBg} ${styles.badgeText} text-[10px] font-semibold leading-none px-1`}
            >
              {count}
            </span>
          ) : (
            <span className="h-[1.15rem]" />
          )}
        </div>
      </DayButton>
    );
  };

  const RdpDropdown = ({
  value,
  onChange,
  options,
  className,
  disabled,
  ...rest
}: RdpDropdownProps) => {
  return (
    <div className="relative inline-block">
      <select
        {...rest}
        disabled={disabled}
        value={value}
        onChange={onChange}
        className={cn(
          "h-9 rounded-md border bg-background px-2 pr-8 text-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:opacity-50",
          "appearance-none [-webkit-appearance:none] [-moz-appearance:none] [background-image:none]",
          className
        )}
      >
        {options?.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* ไอคอนลูกศรด้านขวา */}
      <svg
        viewBox="0 0 20 20"
        className="pointer-events-none absolute right-2 top-1/2 h-4 w-6 -translate-y-1/2 opacity-60"
        fill="currentColor"
      >
        <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.06l3.71-2.83a.75.75 0 0 1 .92 1.18l-4.25 3.25a.75.75 0 0 1-.92 0L5.25 8.41a.75.75 0 0 1-.02-1.2z" />
      </svg>
    </div>
  );
};

  return (
    <div className="flex flex-col gap-2">
      {label && <Label className="px-1">{label}</Label>}

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={`justify-between font-normal ${className}`}
          >
            {selectedDate ? formatToThaiDate(selectedDate) : placeholder}
            <CalendarDays className="h-6 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>

        {/* ⬇️ บังคับความกว้าง + ทับ min-width ของ Radix */}
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={8}
          className="min-w-0 w-[310px] sm:w-[340px] p-3"
        >
          <Calendar
            mode="single"
            captionLayout="dropdown"
            selected={selectedDate}
            disabled={minDate ? { before: minDate } : undefined}
            onSelect={(date) => {
              if (date) onChange?.(formatToISODate(date));
              setOpen(false);
            }}
            formatters={{
              // เดือนย่อไทย + ปี พ.ศ. ใน dropdown
              formatMonthDropdown: (d) =>
                ["ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.","ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค."][d.getMonth()],
              formatYearDropdown: (d) => String(d.getFullYear() + 543),

              formatWeekdayName: (date) => TH_WEEKDAYS[date.getDay()],
            }}
            components={{
              DayButton: CustomDayButton,   // ของเดิมคุณ
              Dropdown: RdpDropdown,        // ✅ ใช้ตัวใหม่ที่ render <option>
            }}
            classNames={{
              // เดือนเดียวเสมอ → ไม่กินความกว้าง
              months: "grid grid-cols-1 gap-3",
              month: "space-y-2",

              // แถวบน: ปุ่มเลื่อน + เดือน/ปี (กึ่งกลาง)
              caption: "relative flex items-center justify-center pt-1 pb-2",
              nav: "absolute inset-x-0 flex items-center justify-between px-4",
              caption_label: "text-base font-semibold tracking-wide",
              caption_dropdowns: "flex items-center gap-2",
              dropdown: "min-w-[4.5rem]",
              dropdown_month: "min-w-[5.25rem]",
              dropdown_year: "min-w-[5rem]",

              // ตารางวัน
              table: "w-full border-collapse",
              head_row: "grid grid-cols-7 gap-1 px-1",
              head_cell:
                "text-muted-foreground text-xs font-medium h-7 flex items-center justify-center rounded-md",

              row: "grid grid-cols-7 gap-1 px-1",
              cell: "relative p-0",

              // fallback ปุ่มวัน (ในกรณีไม่ได้ใช้ CustomDayButton)

              day: `h-10 w-10 mx-auto rounded-lg text-sm inline-flex items-center justify-center transition ${styles.hoverBg} ${styles.hoverText} focus-visible:outline-none aria-selected:opacity-100`,

              day_selected: `${styles.selectedBg} ${styles.selectedText} ${styles.hoverBg} ${styles.hoverText}`,

              day_today: `${styles.todayRing}`,

              day_outside: "text-muted-foreground opacity-50",
              day_disabled: "text-muted-foreground opacity-40",
              day_range_start: "rounded-l-lg",
              day_range_end: "rounded-r-lg",
              day_range_middle:
                "aria-selected:bg-accent aria-selected:text-accent-foreground",
              day_hidden: "invisible",
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
